import { useState, useEffect, useCallback, useRef } from "react";
import api, { formatErr, downloadBlob } from "@/lib/api";
import { fmtDateTime, fmtBytes } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Upload, FileText, Download, Trash2, Loader2 } from "lucide-react";

export default function Attachments({ relatedType, relatedId }) {
  const { user } = useAuth();
  const canWrite = user?.role !== "readonly";
  const canDelete = ["sales_manager", "tenant_owner", "platform_owner"].includes(user?.role);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const inputRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/files?related_type=${relatedType}&related_id=${relatedId}`);
      setFiles(data.items);
    } finally { setLoading(false); }
  }, [relatedType, relatedId]);
  useEffect(() => { load(); }, [load]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("related_type", relatedType);
      fd.append("related_id", relatedId);
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const remove = async () => {
    try { await api.delete(`/files/${confirm.id}`); toast.success("File removed"); setConfirm(null); load(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="attachments">
      <div className="flex items-center justify-between p-4 border-b border-slate-100">
        <h3 className="font-display font-semibold text-slate-800">Attachments</h3>
        {canWrite && (
          <>
            <input ref={inputRef} type="file" className="hidden" onChange={onPick}
              accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt" data-testid="attachment-file-input" />
            <Button size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Upload
            </Button>
          </>
        )}
      </div>
      {loading ? (
        <div className="p-6"><div className="h-4 bg-slate-100 rounded animate-pulse" /></div>
      ) : files.length === 0 ? (
        <p className="text-sm text-slate-400 p-6 text-center">No files yet. Upload agreements, artwork or signed PDFs (max 15 MB).</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-3 p-4" data-testid={`file-${f.id}`}>
              <div className="h-9 w-9 rounded-lg bg-[#00C9E0]/10 flex items-center justify-center shrink-0"><FileText className="h-4 w-4 text-[#0094A8]" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{f.original_filename}</p>
                <p className="text-xs text-slate-400">{fmtBytes(f.size)} · {f.uploaded_by_name} · {fmtDateTime(f.created_at)}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => downloadBlob(`/files/${f.id}/download`, f.original_filename)} data-testid={`download-${f.id}`}><Download className="h-4 w-4" /></Button>
              {canDelete && <Button size="sm" variant="ghost" className="text-[#C42607]" onClick={() => setConfirm(f)} data-testid={`delete-file-${f.id}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          ))}
        </div>
      )}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this file?</AlertDialogTitle>
            <AlertDialogDescription>“{confirm?.original_filename}” will be removed. This is logged in the audit trail.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[#FE421C] hover:bg-[#C42607]" data-testid="confirm-delete-file">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
